import * as React from 'react'
import { Button } from '@/components/ui/button'
import { FormError } from '@/components/ui/form-error'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useAcceptLegal, useCreateFirstVenue } from '@/hooks/useOrganization'
import { LEGAL_VERSIONS } from '@lib/constants/legal'
import { firstVenueInputSchema } from '@lib/input/signup.input'
import { LegalConsent } from './LegalConsent'
import { VenueFields, resolvedVenue, useSignupTimezones, type FirstVenueDraft } from './VenueFields'

/** The billing owner accepts the terms before paying. */
export function AcceptLegalStep() {
  const accept = useAcceptLegal()
  const [checked, setChecked] = React.useState(false)
  return (
    <div className="space-y-3">
      <LegalConsent id="owner-legal" checked={checked} onChange={setChecked} />
      <Button
        type="button"
        onClick={() => accept.mutate({ termsVersion: LEGAL_VERSIONS.terms, privacyVersion: LEGAL_VERSIONS.privacy })}
        disabled={!checked}
        pending={accept.isPending}
      >
        Continue
      </Button>
      {accept.error ? <FormError>{requestErrorMessage(accept.error)}</FormError> : null}
    </div>
  )
}

/** A company created by hand gets its first venue from its billing owner, before paying. */
export function FirstVenueStep() {
  const create = useCreateFirstVenue()
  const offered = useSignupTimezones()
  const [venue, setVenue] = React.useState<FirstVenueDraft>({ name: '', address: '', timezone: '' })
  const [errors, setErrors] = React.useState<Partial<Record<keyof FirstVenueDraft, string>>>({})

  function submit(): void {
    const parsed = firstVenueInputSchema.safeParse(resolvedVenue(venue, offered))
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])))
      return
    }
    setErrors({})
    create.mutate(parsed.data)
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-muted">Add your first venue. You can add more once billing is set up.</p>
      <VenueFields value={venue} onChange={setVenue} errors={errors} offered={offered} />
      <Button pending={create.isPending} type="button" onClick={submit}>
        Add venue
      </Button>
      {create.error ? <FormError>{requestErrorMessage(create.error)}</FormError> : null}
    </div>
  )
}
